import { TokenDetailsFragment } from '@/gql/generated/graphql'
import { useSecondaryBuyerModalState } from '@/hooks/use-secondary-buyer-modal-state'
import { useSecondaryOwnerModalState } from '@/hooks/use-secondary-owner-modal-state'
import { useETHToUSD } from '@/hooks/use-usd'
import { cn, formatUSD } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { Button, buttonVariants } from '@components/button'
import {
  AcceptBidModal,
  BidModal,
  BuyModal,
  CancelListingModal,
  EditListingModal,
  ListModal,
  useBids,
  useListings
} from '@reservoir0x/reservoir-kit-ui'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { ALLOWED_LIST_PLATFORMS, getOpenseaUrl, getPlatformIcon } from '@utils/common'
import { getReservoirCollectionId, getReservoirTokenId, ReservoirBid } from '@utils/reservoir'
import { BodyText } from 'design-configuration/components/responsive-text'
import { env } from 'environments'
import { motion } from 'motion/react'
import { useSession } from 'next-auth/react'
import { useEffect } from 'react'
import { refreshSecondaryToken } from '../_actions/server-refresh-secondary-token'
import { getExpirationString } from '../utils'

export function SecondaryDetails({
  token,
  className,
  initialHighestOffer
}: {
  token: TokenDetailsFragment
  className?: string
  initialHighestOffer: ReservoirBid | null | undefined
}): React.ReactElement {
  const { data: session } = useSession()
  const isOwner = session?.address?.toLowerCase() === token.owner_address

  const {
    data: freshListings,
    mutate,
    isLoading
  } = useListings({
    token: getReservoirTokenId(token.id),
    status: 'active',
    sortBy: 'price'
  })
  const freshListing = freshListings[0] as ReturnType<typeof useListings>['data'][number] | undefined

  const freshListingPriceDecimal = freshListing?.price?.amount?.decimal
  const dbListingPriceDecimal = token.list_price
  const listPriceDecimal = isLoading ? dbListingPriceDecimal : freshListingPriceDecimal
  const listPrice = listPriceDecimal ? parseFloat(Number(listPriceDecimal).toFixed(4)) : null
  const { usd: listingUsd } = useETHToUSD(listPriceDecimal ?? 0)
  const listingUsdDisplay = listingUsd ? parseFloat(listingUsd.toFixed(2)) : null
  const listingCurrencySymbol = token.list_currency_symbol === 'Ξ' ? 'ETH' : token.list_currency_symbol
  const listPlatform = (freshListing?.source?.name as string | undefined) ?? token.list_platform
  const ListingPlatformIcon = getPlatformIcon(listPlatform)
  const listingExpiration = freshListing?.expiration ? freshListing.expiration * 1000 : token.list_expiration_date
  const listingExpirationDate = listingExpiration ? new Date(listingExpiration) : null

  useEffect(() => {
    if (listPriceDecimal !== dbListingPriceDecimal) {
      refreshSecondaryToken(token.contract_address, token.token_id)
    }
  }, [listPriceDecimal, dbListingPriceDecimal])

  const { data: highestOffers } = useBids({
    token: getReservoirTokenId(token.id),
    status: 'active',
    sortBy: 'price'
  })

  // Maybe TODO: We're manually filtering the highest offers to only include
  // allowed platform but it's possible to do this via the API by setting the
  // `sources` parameter to the allowed platform contract addresses. Not sure
  // exactly where to grab these/if there are multiple per platform so
  // sticking with this for now.
  const highestOffer =
    (highestOffers.find((offer) =>
      ALLOWED_LIST_PLATFORMS.includes((offer.source as { name: string } | undefined)?.name ?? '')
    ) as ReturnType<typeof useBids>['data'][number] | undefined) ?? initialHighestOffer

  const highestOfferPriceDecimal = highestOffer?.price?.amount?.decimal
  const highestOfferPrice = highestOfferPriceDecimal ? parseFloat(Number(highestOfferPriceDecimal).toFixed(4)) : null
  const { usd: highestOfferUsd } = useETHToUSD(highestOfferPriceDecimal ?? 0)
  const highestOfferUsdDisplay = highestOfferUsd ? parseFloat(highestOfferUsd.toFixed(2)) : null
  const highestOfferCurrencySymbol = highestOffer?.price?.currency?.symbol
  const highestOfferPlatform = highestOffer?.source?.name as string | undefined
  const HighestOfferPlatformIcon = getPlatformIcon(highestOfferPlatform)
  const highestOfferExpirationDate = highestOffer?.expiration ? new Date(highestOffer.expiration * 1000) : null

  const refetchListings = () => {
    mutate()
  }

  return (
    <div className={className}>
      <div className={cn('gap-x-xs gap-y-md mb-sm flex flex-wrap', className)}>
        <motion.div className="min-w-[180px] flex-1">
          <PriceDetail
            platformIcon={ListingPlatformIcon}
            price={listPrice}
            currencySymbol={listingCurrencySymbol}
            usdDisplay={listingUsdDisplay}
            label="Listing"
            expiration={listingExpirationDate}
            emptyStateText="Not listed for sale"
            link={
              listPlatform?.toLowerCase() === 'opensea'
                ? getOpenseaUrl(token.contract_address, token.token_id)
                : undefined
            }
          />
        </motion.div>
        <motion.div
          className="min-w-[180px] flex-1"
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <PriceDetail
            platformIcon={HighestOfferPlatformIcon}
            price={highestOfferPrice}
            currencySymbol={highestOfferCurrencySymbol}
            usdDisplay={highestOfferUsdDisplay}
            label="Top Offer"
            expiration={highestOfferExpirationDate}
            emptyStateText="No offers"
            link={
              highestOfferPlatform?.toLowerCase() === 'opensea'
                ? getOpenseaUrl(token.contract_address, token.token_id)
                : undefined
            }
          />
        </motion.div>
      </div>
      <div>
        {isOwner ? (
          <SecondaryOwnerActions
            token={token}
            highestOffer={highestOffer}
            listingInfo={freshListing}
            resetListingData={refetchListings}
          />
        ) : (
          <SecondaryBuyerActions token={token} listingInfo={freshListing} resetListingData={refetchListings} />
        )}
      </div>
    </div>
  )
}

function SecondaryOwnerActions({
  token,
  highestOffer,
  listingInfo,
  resetListingData
}: {
  token: TokenDetailsFragment
  highestOffer: ReservoirBid | null | undefined
  listingInfo: ReturnType<typeof useListings>['data'][number] | undefined
  resetListingData: () => void
}): React.ReactElement {
  const isListed = Boolean(listingInfo?.id)
  const {
    listModalOpen,
    acceptBidModalOpen,
    openListingModal,
    openAcceptBidModal,
    closeListingModal,
    closeAcceptBidModal
  } = useSecondaryOwnerModalState()

  const listPlatform = listingInfo?.source?.name as string | undefined
  return (
    <div className={cn('gap-xs flex flex-col min-[430px]:items-center', isListed ? '' : 'min-[400px]:flex-row')}>
      {!isListed ? (
        <ListModal
          trigger={
            <Button
              colorTheme="secondary"
              branded
              className="bg-surface flex-1"
              onClick={(e) => {
                openListingModal()
                trackButtonClicked(TrackedButtonNames.createListing, {
                  token,
                  customProperties: {
                    location: 'Token Page Secondary Details'
                  }
                })
                e.preventDefault()
              }}
            >
              Create Listing
            </Button>
          }
          oracleEnabled
          openState={[
            listModalOpen,
            (value: boolean | ((prev: boolean) => boolean)) => {
              const newValue = typeof value === 'boolean' ? value : value(listModalOpen)

              if (newValue) {
                openListingModal()
                return
              } else {
                closeListingModal()
              }
            }
          ]}
          tokenId={token.token_id}
          chainId={env.chain.id}
          onListingComplete={() => {
            resetListingData()
          }}
          collectionId={getReservoirCollectionId(token.contract_address, token.project.project_id)}
        />
      ) : null}
      {highestOffer && (
        <AcceptBidModal
          openState={[
            acceptBidModalOpen,
            (value: boolean | ((prev: boolean) => boolean)) => {
              const newValue = typeof value === 'boolean' ? value : value(acceptBidModalOpen)

              if (newValue) {
                openAcceptBidModal()
                return
              } else {
                closeAcceptBidModal()
              }
            }
          ]}
          trigger={
            <Button
              colorTheme="primary"
              branded
              className="w-full flex-1"
              onClick={(e) => {
                openAcceptBidModal()
                trackButtonClicked(TrackedButtonNames.acceptOffer, {
                  token,
                  customProperties: {
                    location: 'Token Page Secondary Details'
                  }
                })
                e.preventDefault()
              }}
            >
              Accept Offer
            </Button>
          }
          tokens={[
            {
              tokenId: token.token_id,
              bidIds: [highestOffer.id],
              collectionId: getReservoirCollectionId(token.contract_address, token.project.project_id)
            }
          ]}
          onBidAccepted={() => {
            resetListingData()
          }}
        />
      )}
      {isListed ? (
        listPlatform?.toLowerCase() !== 'opensea' ? (
          <div className="gap-xs flex w-full flex-col sm:flex-row">
            <EditListingModal
              trigger={
                <Button
                  colorTheme="secondary"
                  branded
                  className="bg-surface flex-1"
                  onClick={(e) => {
                    trackButtonClicked(TrackedButtonNames.editListing, {
                      token,
                      customProperties: {
                        location: 'Token Page Secondary Details'
                      }
                    })
                    e.preventDefault()
                  }}
                >
                  Edit Listing
                </Button>
              }
              listingId={listingInfo?.id}
              tokenId={token.token_id}
              chainId={env.chain.id}
              collectionId={getReservoirCollectionId(token.contract_address, token.project.project_id)}
              onEditListingComplete={() => {
                resetListingData()
              }}
            />
            <CancelListingModal
              trigger={
                <Button
                  colorTheme="secondary"
                  branded
                  className="bg-surface flex-1"
                  onClick={() => {
                    trackButtonClicked(TrackedButtonNames.cancelListing, {
                      token,
                      customProperties: {
                        location: 'Token Page Secondary Details'
                      }
                    })
                  }}
                >
                  Cancel Listing
                </Button>
              }
              listingId={listingInfo?.id}
              onCancelComplete={() => {
                resetListingData()
              }}
            />
          </div>
        ) : (
          <AllPurposeLink
            href={getOpenseaUrl(token.contract_address, token.token_id)}
            className={cn(buttonVariants({ colorTheme: 'secondary', branded: true }), 'bg-surface px-xs flex-1')}
            onClick={() => {
              trackButtonClicked(TrackedButtonNames.editListing, {
                token,
                customProperties: {
                  location: 'Token Page Secondary Details',
                  Opensea: true
                }
              })
            }}
          >
            Edit Listing
          </AllPurposeLink>
        )
      ) : null}
    </div>
  )
}

function SecondaryBuyerActions({
  token,
  listingInfo,
  resetListingData
}: {
  token: TokenDetailsFragment
  listingInfo: ReturnType<typeof useListings>['data'][number] | undefined
  resetListingData: () => void
}): React.ReactElement {
  const { bidModalOpen, buyModalOpen, openBidModal, openBuyModal, closeBidModal, closeBuyModal } =
    useSecondaryBuyerModalState({ token })

  const isListed = Boolean(listingInfo?.id)

  return (
    <div className="gap-xs flex flex-col min-[430px]:flex-row min-[430px]:items-center">
      {isListed ? (
        <BuyModal
          trigger={
            <Button
              colorTheme="primary"
              branded
              className="flex-1"
              onClick={(e) => {
                openBuyModal()
                trackButtonClicked(TrackedButtonNames.buyNow, {
                  token,
                  customProperties: {
                    location: 'Token Page Secondary Details'
                  }
                })
                e.preventDefault()
              }}
            >
              Buy Now
            </Button>
          }
          token={getReservoirTokenId(token.id)}
          onConnectWallet={() => {
            return
          }}
          onPurchaseComplete={() => {
            resetListingData()
          }}
          openState={[
            buyModalOpen,
            (value: boolean | ((prev: boolean) => boolean)) => {
              const newValue = typeof value === 'boolean' ? value : value(buyModalOpen)

              if (newValue) {
                openBuyModal()
                return
              } else {
                closeBuyModal()
              }
            }
          ]}
        />
      ) : null}
      <BidModal
        tokenId={token.token_id}
        chainId={env.chain.id}
        collectionId={getReservoirCollectionId(token.contract_address, token.project.project_id)}
        oracleEnabled
        trigger={
          <Button
            colorTheme="secondary"
            branded
            className="bg-surface flex-1"
            onClick={(e) => {
              openBidModal()
              trackButtonClicked(TrackedButtonNames.makeOffer, {
                token,
                customProperties: {
                  location: 'Token Page Secondary Details'
                }
              })
              e.preventDefault()
            }}
            tabIndex={bidModalOpen ? -1 : 0}
          >
            Make Offer
          </Button>
        }
        openState={[
          bidModalOpen,
          (value: boolean | ((prev: boolean) => boolean)) => {
            const newValue = typeof value === 'boolean' ? value : value(bidModalOpen)

            if (newValue) {
              openBidModal()
              return
            } else {
              closeBidModal()
            }
          }
        ]}
      />
    </div>
  )
}

function PriceDetail({
  platformIcon: PlatformIcon,
  price,
  currencySymbol,
  usdDisplay,
  label,
  expiration,
  emptyStateText,
  link
}: {
  platformIcon?: React.FC<React.SVGProps<SVGSVGElement>> | null
  price?: number | null
  currencySymbol: string | null | undefined
  usdDisplay: number | null
  label: string
  expiration: Date | null
  emptyStateText: string
  link?: string
}) {
  const expirationString = expiration ? getExpirationString(expiration) : null
  const hasPriceDetails = PlatformIcon && price != null

  const priceContent = hasPriceDetails ? (
    <>
      <PlatformIcon className="fill-on-surface h-3 w-3" />
      <BodyText className="font-medium">
        {price} {currencySymbol}
      </BodyText>
      {usdDisplay != null ? (
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-on-surface-variant text-detail">
          ${formatUSD(usdDisplay)} USD
        </motion.span>
      ) : null}
    </>
  ) : (
    <BodyText className="text-on-surface-variant">{emptyStateText}</BodyText>
  )

  return (
    <div>
      <p className="text-detail text-on-surface-variant mb-xs">
        <span className="font-medium uppercase">{label}</span> {expirationString && <span>{expirationString}</span>}
      </p>
      {link ? (
        <AllPurposeLink href={link} className={cn('gap-2xs flex items-baseline')}>
          {priceContent}
        </AllPurposeLink>
      ) : (
        <div className={cn('gap-2xs flex items-baseline')}>{priceContent}</div>
      )}
    </div>
  )
}
